import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors, FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, FormsModule],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css']
})
export class SignupComponent implements OnInit, OnDestroy {
  signupForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  credentials: any = null;
  currentStep = 1;
  showShakeAnimation = false;
  showPassword = false;
  parentName = '';
  parentReferralCode = '';
  spouseReferralCode = '';
  
  // Searchable dropdowns for relatives
  parentSearchTerm = '';
  spouseSearchTerm = '';
  parentSearchResults: any[] = [];
  spouseSearchResults: any[] = [];
  showParentDropdown = false;
  showSpouseDropdown = false;
  selectedParent: any = null;
  selectedSpouse: any = null;
  isLoadingRelatives = false;
  private searchTimeout: any = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.signupForm = this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      email: ['', [this.optionalEmailValidator]],
      mobileNumber: ['', [this.optionalMobileValidator]],
      dateOfBirth: ['', Validators.required],
      gender: ['', Validators.required],
      maritalStatus: [''],
      isDeceased: [false],
      qualification: [''],
      profession: [''],
      location: [''],
      parentReferralCode: [''], // Optional
      spouseReferralCode: [''], // Optional
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      acceptPrivacyAgreement: [false, Validators.requiredTrue]
    });

    // Don't generate credentials here - wait for ngOnInit to avoid double generation

    // Both referral codes are optional; validation enforced on submit
  }

  ngOnInit(): void {
    // Check for query parameters from referral links or hierarchy navigation
    this.route.queryParams.subscribe(params => {
      if (params['parentReferralCode']) {
        this.parentReferralCode = params['parentReferralCode'];
        this.parentName = params['parentName'] || '';
        
        // Autofill the parent referral code
        this.signupForm.patchValue({
          parentReferralCode: this.parentReferralCode
        });
        
        // If parent name is provided, set it in search term and create a selected parent object
        if (this.parentName) {
          this.parentSearchTerm = this.parentName;
          // Create a pseudo-relative object for display purposes
          this.selectedParent = {
            referralCode: this.parentReferralCode,
            firstName: this.parentName.split(' ')[0] || '',
            lastName: this.parentName.split(' ').slice(1).join(' ') || ''
          };
        }
        
        // Scroll to family information section after a brief delay to ensure DOM is ready
        setTimeout(() => {
          const familySection = document.querySelector('.form-section');
          if (familySection) {
            familySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
      
      if (params['spouseReferralCode']) {
        const spouseName = params['spouseName'] || '';
        const spouseCode = params['spouseReferralCode'];
        
        this.spouseReferralCode = spouseCode;
        this.signupForm.patchValue({
          spouseReferralCode: spouseCode
        });
        
        if (spouseName) {
          this.spouseSearchTerm = spouseName;
          // Create a pseudo-relative object for display purposes
          this.selectedSpouse = {
            referralCode: spouseCode,
            firstName: spouseName.split(' ')[0] || '',
            lastName: spouseName.split(' ').slice(1).join(' ') || ''
          };
        }
        
        // Scroll to family information section after a brief delay
        setTimeout(() => {
          const familySection = document.querySelector('.form-section');
          if (familySection) {
            familySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    });

    // Generate credentials on initialization
    this.generateDefaultCredentials();

    // Watch for changes in first name or last name to regenerate credentials
    this.signupForm.get('firstName')?.valueChanges.subscribe(() => {
      this.generateDefaultCredentials();
    });
    this.signupForm.get('lastName')?.valueChanges.subscribe(() => {
      this.generateDefaultCredentials();
    });

    // Initialize Google Sign-In - retry if script not loaded
    this.initializeGoogleSignIn();
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', (event: any) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-dropdown-container')) {
        this.showParentDropdown = false;
        this.showSpouseDropdown = false;
      }
    });
  }
  
  searchRelatives(searchTerm: string, type: 'parent' | 'spouse'): void {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // If search term is too short, clear results immediately
    if (!searchTerm || searchTerm.trim().length < 2) {
      if (type === 'parent') {
        this.parentSearchResults = [];
        this.showParentDropdown = false;
      } else {
        this.spouseSearchResults = [];
        this.showSpouseDropdown = false;
      }
      this.isLoadingRelatives = false;
      return;
    }

    // Debounce search - wait 300ms after user stops typing
    this.searchTimeout = setTimeout(() => {
      this.performSearch(searchTerm.trim(), type);
    }, 300);
  }

  private performSearch(searchTerm: string, type: 'parent' | 'spouse'): void {
    if (searchTerm.length < 2) {
      return;
    }
    
    this.isLoadingRelatives = true;
    
    // Show dropdown while loading
    if (type === 'parent') {
      this.showParentDropdown = true;
    } else {
      this.showSpouseDropdown = true;
    }
    
    // Fetch relatives from public endpoint
    this.authService.getPublicRelatives(searchTerm).subscribe({
      next: (response: any) => {
        this.isLoadingRelatives = false;
        console.log('Search response received:', response);
        
        // Handle different response structures
        // Backend returns: { success: true, data: { relatives: [...] } }
        let relatives = [];
        if (response?.data?.relatives) {
          relatives = response.data.relatives;
        } else if (response?.relatives) {
          relatives = response.relatives;
        } else if (Array.isArray(response?.data)) {
          relatives = response.data;
        } else if (Array.isArray(response)) {
          relatives = response;
        }
        
        console.log('Parsed relatives:', relatives);
        
        if (type === 'parent') {
          this.parentSearchResults = relatives;
          this.showParentDropdown = relatives.length > 0 || searchTerm.length >= 2;
        } else {
          this.spouseSearchResults = relatives;
          this.showSpouseDropdown = relatives.length > 0 || searchTerm.length >= 2;
        }
      },
      error: (error) => {
        this.isLoadingRelatives = false;
        console.error('Error searching relatives:', error);
        
        if (type === 'parent') {
          this.parentSearchResults = [];
          this.showParentDropdown = false;
        } else {
          this.spouseSearchResults = [];
          this.showSpouseDropdown = false;
        }
        
        // Show user-friendly message in console (could be displayed in UI if needed)
        console.log('Note: Unable to search relatives. You can enter referral codes manually or set relationships after login.');
      }
    });
  }
  
  selectParent(relative: any): void {
    this.selectedParent = relative;
    this.parentSearchTerm = `${relative.firstName} ${relative.lastName}`;
    this.signupForm.patchValue({
      parentReferralCode: relative.referralCode
    });
    this.showParentDropdown = false;
    this.parentSearchResults = [];
  }
  
  selectSpouse(relative: any): void {
    this.selectedSpouse = relative;
    this.spouseSearchTerm = `${relative.firstName} ${relative.lastName}`;
    this.signupForm.patchValue({
      spouseReferralCode: relative.referralCode
    });
    this.showSpouseDropdown = false;
    this.spouseSearchResults = [];
  }
  
  clearParent(): void {
    this.selectedParent = null;
    this.parentSearchTerm = '';
    this.signupForm.patchValue({
      parentReferralCode: ''
    });
  }
  
  clearSpouse(): void {
    this.selectedSpouse = null;
    this.spouseSearchTerm = '';
    this.signupForm.patchValue({
      spouseReferralCode: ''
    });
  }

  initializeGoogleSignIn(retryCount = 0): void {
    const maxRetries = 20; // Increased retries
    const retryDelay = 300; // Increased delay

    // Check if Google script is loaded and Client ID is configured
    if (typeof (window as any).google !== 'undefined' && 
        typeof (window as any).google.accounts !== 'undefined' &&
        typeof (window as any).google.accounts.id !== 'undefined' &&
        environment.googleClientId && 
        environment.googleClientId !== 'YOUR_GOOGLE_CLIENT_ID_HERE') {
      
      try {
        const buttonElement = document.getElementById('google-signup-button');
        if (!buttonElement) {
          console.error('Google sign-up button element not found');
          if (retryCount < maxRetries) {
            setTimeout(() => this.initializeGoogleSignIn(retryCount + 1), retryDelay);
          }
          return;
        }

        // Initialize Google Sign-In
        (window as any).google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: any) => this.handleGoogleSignIn(response),
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Render the button
        (window as any).google.accounts.id.renderButton(
          buttonElement,
          {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'signup_with',
            locale: 'en'
          }
        );
        
        console.log('Google Sign-Up button initialized successfully');
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        // Retry on error
        if (retryCount < maxRetries) {
          setTimeout(() => this.initializeGoogleSignIn(retryCount + 1), retryDelay);
        }
      }
    } else if (retryCount < maxRetries) {
      // Retry if script not loaded yet
      setTimeout(() => this.initializeGoogleSignIn(retryCount + 1), retryDelay);
    } else {
      console.warn('Google Sign-In script failed to load after multiple retries');
      console.warn('Google object:', typeof (window as any).google);
      console.warn('Google Client ID:', environment.googleClientId);
    }
  }

  handleGoogleSignIn(response: any): void {
    if (!response.credential) {
      this.errorMessage = 'Google sign-up failed. Please try again.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Get referral codes from form or query params
    const parentReferralCode = this.signupForm.get('parentReferralCode')?.value || this.parentReferralCode || this.route.snapshot.queryParams['parentReferralCode'];
    const spouseReferralCode = this.signupForm.get('spouseReferralCode')?.value || this.route.snapshot.queryParams['spouseReferralCode'];
    const acceptPrivacyAgreement = this.signupForm.get('acceptPrivacyAgreement')?.value || false;

    const googleAuthRequest = {
      idToken: response.credential,
      parentReferralCode: parentReferralCode || undefined,
      spouseReferralCode: spouseReferralCode || undefined,
      acceptPrivacyAgreement: acceptPrivacyAgreement || true // Default to true for Google sign-up
    };

    console.log('🔐 Initiating Google sign-up...');

    this.authService.googleAuth(googleAuthRequest).subscribe({
      next: (authResponse: any) => {
        console.log('✅ Google sign-up successful:', authResponse);
        
        // Verify response structure
        if (!authResponse || !authResponse.success) {
          console.error('❌ Invalid response structure:', authResponse);
          this.isLoading = false;
          this.errorMessage = 'Sign-up failed. Invalid response from server.';
          return;
        }

        if (authResponse.data?.requiresPrivacyAgreement) {
          console.log('⚠️ Privacy agreement required');
          this.isLoading = false;
          // If privacy agreement is required, navigate to login or show popup
          // For now, redirect to login with a message
          this.errorMessage = 'Please accept the privacy agreement to continue.';
          this.router.navigate(['/login'], {
            queryParams: { message: 'Please complete privacy agreement setup' }
          });
          return;
        }

        // Verify token is stored and authentication state is ready
        const verifyAuth = () => {
          const token = this.authService.getToken();
          const user = this.authService.getCurrentUser();
          const isAuthenticated = this.authService.isAuthenticated();
          
          console.log('🔑 Token stored:', !!token);
          console.log('👤 User stored:', !!user);
          console.log('🔐 Is authenticated:', isAuthenticated);
          
          return token && user && isAuthenticated;
        };

        // Success - user is registered and logged in
        if (authResponse.data?.isNewUser) {
          this.successMessage = 'Successfully registered with Google! Redirecting...';
        } else {
          this.successMessage = 'Successfully logged in with Google! Redirecting...';
        }

        // Wait for auth state to be ready, with retries
        let retries = 0;
        const maxRetries = 10;
        const checkAndNavigate = () => {
          if (verifyAuth() || retries >= maxRetries) {
            this.isLoading = false;
            
            if (!verifyAuth()) {
              console.error('❌ Auth state verification failed after retries');
              this.errorMessage = 'Sign-up successful but state verification failed. Please refresh the page.';
              return;
            }
            
            // Determine destination
            let destination = '/home';
            if (authResponse.data && authResponse.data.isAdmin) {
              destination = '/dashboard';
              console.log('🚀 Redirecting to admin dashboard');
            } else {
              console.log('🚀 Redirecting to home');
            }
            
            // Navigate using navigateByUrl for more explicit navigation
            console.log('🔀 Attempting router navigation to:', destination);
            this.router.navigateByUrl(destination, { skipLocationChange: false })
              .then(success => {
                if (success) {
                  console.log('✅ Router navigation successful to:', destination);
                  // Navigation successful, no need for fallback
                } else {
                  console.warn('⚠️ Router navigation returned false, using fallback');
                  // Fallback: force navigation with page reload only if router fails
                  setTimeout(() => {
                    console.log('🔄 Forcing navigation via window.location.href');
                    window.location.href = destination;
                  }, 100);
                }
              })
              .catch(err => {
                console.error('❌ Router navigation error:', err);
                // Fallback: force navigation with page reload on error
                console.log('🔄 Using fallback navigation to:', destination);
                setTimeout(() => {
                  window.location.href = destination;
                }, 100);
              });
          } else {
            retries++;
            setTimeout(checkAndNavigate, 50);
          }
        };
        
        // Start checking after a brief delay
        setTimeout(checkAndNavigate, 50);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('❌ Google sign-up error:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          error: error.error
        });
        
        if (error.status === 401 || error.status === 403) {
          this.errorMessage = error.error?.message || 'Sign-up failed. Please check your Google account.';
        } else if (error.status === 0) {
          this.errorMessage = 'Unable to connect to server. Please check your internet connection.';
        } else {
          this.errorMessage = error.error?.message || 'Google sign-up failed. Please try again.';
        }
      }
    });
  }

  optionalEmailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.trim() === '') {
      return null; // Allow empty values
    }
    // If value is provided, validate email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(value) ? null : { email: true };
  }

  optionalMobileValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.trim() === '') {
      return null; // Allow empty values
    }
    // If value is provided, validate it's a reasonable phone number (at least 10 digits)
    const phonePattern = /^[\d\s\-\+\(\)]+$/;
    const digitsOnly = value.replace(/\D/g, '');
    if (!phonePattern.test(value) || digitsOnly.length < 10) {
      return { mobile: true };
    }
    return null;
  }

  // Step navigation methods removed - using single-page form now

  generateDefaultCredentials(): void {
    const firstName = this.signupForm.get('firstName')?.value || '';
    const lastName = this.signupForm.get('lastName')?.value || '';
    const email = this.signupForm.get('email')?.value || '';
    
    // Generate username from first name and last name
    const username = firstName && lastName 
      ? `${firstName.toLowerCase()}${lastName.toLowerCase()}${Math.floor(Math.random() * 1000)}`
      : email.split('@')[0] + Math.floor(Math.random() * 1000);
    
    // Generate a secure password
    const password = this.generateSecurePassword();
    
    this.signupForm.patchValue({
      username: username,
      password: password
    });
  }

  generateSecurePassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char
    
    // Fill the rest randomly
    for (let i = 4; i < 12; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  regenerateCredentials(): void {
    this.generateDefaultCredentials();
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  validateReferralCode(): void {
    const parentCode = this.signupForm.get('parentReferralCode')?.value?.trim();
    const spouseCode = this.signupForm.get('spouseReferralCode')?.value?.trim();
  }

  // Step validation method removed - using single-page form now

  onSubmit(): void {
    // Custom validation: at least one of email or mobile number must be provided
    const email = this.signupForm.get('email')?.value?.trim();
    const mobileNumber = this.signupForm.get('mobileNumber')?.value?.trim();
    
    if (!email && !mobileNumber) {
      this.errorMessage = 'Please provide at least an email address or mobile number to register';
      this.signupForm.get('email')?.markAsTouched();
      this.signupForm.get('mobileNumber')?.markAsTouched();
      this.showShakeAnimation = true;
      setTimeout(() => {
        this.showShakeAnimation = false;
      }, 500);
      return;
    }

    if (this.signupForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.credentials = null;
      this.showShakeAnimation = false;

      const formData = this.signupForm.value;
      
      // Fix date format - ensure proper date conversion
      let dateOfBirth: string;
      try {
        const date = new Date(formData.dateOfBirth);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid date');
        }
        dateOfBirth = date.toISOString();
      } catch (error) {
        this.errorMessage = 'Invalid date format. Please check your date of birth.';
        this.isLoading = false;
        return;
      }

      const signupData: any = {
        firstName: formData.firstName,
        middleName: formData.middleName || null,
        lastName: formData.lastName,
        email: formData.email && formData.email.trim() !== '' ? formData.email : null,
        mobileNumber: formData.mobileNumber && formData.mobileNumber.trim() !== '' ? formData.mobileNumber.trim() : null,
        dateOfBirth: dateOfBirth,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus && formData.maritalStatus.trim() !== '' ? formData.maritalStatus : null,
        isDeceased: formData.isDeceased || false,
        qualification: formData.qualification && formData.qualification.trim() !== '' ? formData.qualification : null,
        profession: formData.profession && formData.profession.trim() !== '' ? formData.profession : null,
        location: formData.location && formData.location.trim() !== '' ? formData.location : null,
        parentReferralCode: formData.parentReferralCode && formData.parentReferralCode.trim() !== '' ? formData.parentReferralCode : null,
        spouseReferralCode: formData.spouseReferralCode && formData.spouseReferralCode.trim() !== '' ? formData.spouseReferralCode : null,
        username: formData.username,
        password: formData.password,
        acceptPrivacyAgreement: formData.acceptPrivacyAgreement,
        isAdminCreated: false
      };

      // Automatically determine relationship type based on referral codes
      let relationshipType = null;
      if (formData.parentReferralCode && formData.parentReferralCode.trim() !== '') {
        relationshipType = 'Child'; // If parentReferralCode is provided, the new person becomes a child of the referral code holder
      } else if (formData.spouseReferralCode && formData.spouseReferralCode.trim() !== '') {
        relationshipType = 'Spouse'; // If spouseReferralCode is provided, it's a spouse relationship
      }
      
      // Add relationship type to signup data
      signupData.relationshipType = relationshipType;


      this.authService.signup(signupData).subscribe({
        next: (response) => {
          this.isLoading = false;
          this.successMessage = 'Account created successfully! Please save your credentials.';
          
          // Handle different response structures
          if ((response as any).data) {
            // If response has a data property
            this.credentials = {
              username: (response as any).data.username || response.username,
              password: (response as any).data.password || response.password,
              referralCode: (response as any).data.referralCode || response.referralCode
            };
          } else {
            // Direct response structure
            this.credentials = {
              username: response.username,
              password: response.password,
              referralCode: response.referralCode
            };
          }
          
          // Redirect to login after 10 seconds (increased delay)
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 10000);
        },
        error: (error) => {
          this.isLoading = false;
          this.showShakeAnimation = true;
          
          // Reset shake animation after it completes
          setTimeout(() => {
            this.showShakeAnimation = false;
          }, 500);

          
          // Handle different error types
          if (error.status === 400) {
            // Show more specific error message for 400 errors
            if (error.error && error.error.message) {
              this.errorMessage = error.error.message;
            } else if (error.error && error.error.errors) {
              // Handle validation errors
              const validationErrors = error.error.errors;
              const errorMessages = Object.keys(validationErrors).map(key => 
                `${key}: ${validationErrors[key].join(', ')}`
              );
              this.errorMessage = `Validation errors: ${errorMessages.join('; ')}`;
            } else {
              this.errorMessage = 'Please check your information and try again.';
            }
          } else if (error.status === 409) {
            this.errorMessage = 'An account with this email already exists.';
          } else if (error.status === 0) {
            this.errorMessage = 'Unable to connect to server. Please check your internet connection.';
          } else {
            this.errorMessage = error.error?.message || 'Signup failed. Please try again.';
          }
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.signupForm.markAllAsTouched();
      this.showShakeAnimation = true;
      
      setTimeout(() => {
        this.showShakeAnimation = false;
      }, 500);
    }
  }

  ngOnDestroy(): void {
    // Clean up search timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
}

